// src/utils/debugHelper.js - CREATE THIS NEW FILE

class DebugHelper {
    constructor() {
        this.callCounts = new Map();
        this.callStack = [];
        this.startTime = Date.now();
    }

    // Track where API calls are coming from
    trackApiCall(url, method = 'GET', callerInfo = '') {
        const key = `${method} ${url}`;
        const count = this.callCounts.get(key) || 0;
        this.callCounts.set(key, count + 1);

        // Get stack trace to see where this call came from
        const stack = new Error().stack;
        const caller = this.extractCaller(stack);

        this.callStack.push({
            timestamp: Date.now(),
            url,
            method,
            caller,
            callerInfo,
            count: count + 1
        });

        // Alert if too many calls to same endpoint
        if (count > 5) {
            console.warn(`🚨 EXCESSIVE CALLS DETECTED: ${key} called ${count + 1} times!`);
            console.warn('Stack trace:', stack);
            console.warn('Recent calls:', this.getRecentCalls(url, 10));
        }
    }

    // Extract meaningful caller info from stack trace
    extractCaller(stack) {
        const lines = stack.split('\n');
        // Skip the first few lines (Error, trackApiCall, etc.)
        for (let i = 3; i < Math.min(lines.length, 8); i++) {
            const line = lines[i];
            if (line.includes('.js:') || line.includes('.jsx:') || line.includes('.ts:') || line.includes('.tsx:')) {
                // Extract filename and line number
                const match = line.match(/([^\/\\]+\.(js|jsx|ts|tsx)):(\d+):(\d+)/);
                if (match) {
                    return `${match[1]}:${match[3]}`;
                }
            }
        }
        return 'unknown';
    }

    // Get recent calls to a specific URL
    getRecentCalls(url, limit = 5) {
        return this.callStack
            .filter(call => call.url === url)
            .slice(-limit)
            .map(call => ({
                timeAgo: `${Math.floor((Date.now() - call.timestamp) / 1000)}s ago`,
                caller: call.caller,
                callerInfo: call.callerInfo
            }));
    }

    // Get summary report
    getReport() {
        const runtime = Math.floor((Date.now() - this.startTime) / 1000);
        const totalCalls = this.callStack.length;

        console.log('📊 API CALL ANALYSIS REPORT');
        console.log('='.repeat(50));
        console.log(`Runtime: ${runtime} seconds`);
        console.log(`Total API calls: ${totalCalls}`);
        console.log(`Average calls per second: ${(totalCalls / runtime).toFixed(2)}`);
        console.log('');

        console.log('📈 CALL COUNTS BY ENDPOINT:');
        const sortedCounts = Array.from(this.callCounts.entries())
            .sort((a, b) => b[1] - a[1]);

        sortedCounts.forEach(([endpoint, count]) => {
            console.log(`${count.toString().padStart(4)}x ${endpoint}`);
        });

        console.log('');
        console.log('🔥 TOP PROBLEM ENDPOINTS:');
        const problems = sortedCounts.filter(([_, count]) => count > 5);

        if (problems.length === 0) {
            console.log('✅ No excessive calls detected');
        } else {
            problems.forEach(([endpoint, count]) => {
                console.log(`🚨 ${endpoint}: ${count} calls`);
                const recentCalls = this.getRecentCalls(endpoint.split(' ')[1], 3);
                recentCalls.forEach(call => {
                    console.log(`   - ${call.timeAgo} from ${call.caller}`);
                });
            });
        }

        console.log('');
        console.log('⏰ RECENT CALL TIMELINE (last 20):');
        const recentCalls = this.callStack.slice(-20);
        recentCalls.forEach(call => {
            const timeAgo = Math.floor((Date.now() - call.timestamp) / 1000);
            console.log(`${timeAgo}s ago: ${call.method} ${call.url} (${call.caller})`);
        });
    }

    // Clear data
    reset() {
        this.callCounts.clear();
        this.callStack = [];
        this.startTime = Date.now();
    }
}

// Create singleton
const debugHelper = new DebugHelper();

// TEMPORARILY DISABLED: Console.log wrapping to prevent infinite loops
// const originalLog = console.log;
// console.log = function (...args) {
//     const message = args.join(' ');

//     // Catch axios request logs
//     if (message.includes('API Request:')) {
//         try {
//             const logData = args.find(arg => typeof arg === 'object' && arg.url);
//             if (logData) {
//                 debugHelper.trackApiCall(logData.url, logData.method, 'axios-request');
//             }
//         } catch (e) {
//             // Ignore parsing errors
//         }
//     }

//     // Catch "Public card retrieved" logs 
//     if (message.includes('Public card retrieved successfully')) {
//         debugHelper.trackApiCall('/public-card', 'GET', 'public-card-fetch');
//     }

//     originalLog.apply(console, args);
// };

export default debugHelper;

// EASY USAGE - Add this to any component to start monitoring:
/*

import debugHelper from '../utils/debugHelper.js';

// In your component or App.js:
useEffect(() => {
  // Start monitoring
  console.log('🔍 Starting API call monitoring...');
  
  // After 30 seconds, show report
  const timer = setTimeout(() => {
    debugHelper.getReport();
  }, 30000);
  
  return () => clearTimeout(timer);
}, []);

// Or manually check anytime in console:
// window.debugHelper = debugHelper;
// Then in browser console: debugHelper.getReport()

*/

// Add to window for easy browser console access
if (typeof window !== 'undefined') {
    window.debugHelper = debugHelper;
}

/*
IMPLEMENTATION STEPS:

1. Add this file to your utils folder

2. In your main App.js or any component, add:
   import debugHelper from './utils/debugHelper.js';

3. Start monitoring (add this useEffect to App.js):
   useEffect(() => {
     console.log('🔍 Starting API monitoring...');
     setTimeout(() => debugHelper.getReport(), 30000);
   }, []);

4. In browser console, you can run:
   debugHelper.getReport()

This will tell you:
✅ Which endpoints are called most
✅ Where the calls are coming from (file:line)  
✅ Timeline of recent calls
✅ Exact count per endpoint

*/ 